// functionVersions.js
// Client access to published Function versions and the advisory edit lease.
//
// A Function is edited as a mutable draft (the head document plus its `blocks`
// subcollection) and executes only as an immutable published version at
// `users/{company}/data_functions/{function_id}/versions/{n}`. Every run pins
// the exact version it was reviewed against, so every run-facing surface — the
// confirmation dialog, the parameter panel, View instance forms and Automation
// editing — reads its contract from that one version document rather than from
// the draft.
//
// The edit lease is advisory only: it prevents accidental co-editing through the
// builder UI, can always be taken over with confirmation, and expires on its own
// when a session crashes. Execution safety never depends on it.

import { isResourceDeleting } from '../utils/resourceLifecycle';
import { doc, getDoc, getDocFromServer } from 'firebase/firestore';
import { db } from './firebase';
import { outputHasExternalEffect } from '../utils/outputRegistry';
import { parameterCompatibilityErrors, prepareRunParameters } from '../utils/functionParameterUtils';
import {
  acquireEditLease as acquireLease,
  takeOverEditLease as takeOverLease,
  heartbeatEditLease as heartbeatLease,
  releaseEditLease as releaseLease,
} from './editLease';

// Lease lifetime and heartbeat cadence live with the lease (services/editLease.js).
export { LEASE_TTL_MS, LEASE_HEARTBEAT_MS } from './editLease';

// The canonical actor the publish transaction records when it resets an
// Automation's Email permission, so an audit trail can tell a machine reset
// apart from a person's deliberate change.
export const PUBLISH_ACTOR = 'system:publish';

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

// A published version number is a positive integer. Anything else (null, a
// string, a float, a boolean) means "not published".
export const isPublishedVersion = (value) => Number.isInteger(value) && value >= 1;

// Runnable means: published, with no missing dependency or deletion in progress. This
// one rule replaces every historical `status === 'Final' && validated` check.
export const isFunctionRunnable = (functionDoc) => (
  isPublishedVersion(functionDoc?.published_version) && functionDoc?.invalid !== true
  && !isResourceDeleting(functionDoc)
);

// How an interrupted publish stream actually ended, decided from the head
// read before and after the attempt (both via readFunctionHead). The backend
// commits the publish transaction before streaming its terminal event, so a
// dropped connection does NOT prove nothing changed (H48).
export const classifyAmbiguousPublishEnd = (preHead, postHead) => {
  if (!preHead || !postHead) return { kind: 'unknown' };
  const before = preHead.publishedVersion ?? null;
  const after = postHead.publishedVersion ?? null;
  if (isPublishedVersion(after) && after !== before) {
    return { kind: 'published', publishedVersion: after };
  }
  if (
    isPublishedVersion(after)
    && preHead.hasUnpublishedChanges === true
    && postHead.hasUnpublishedChanges === false
  ) {
    return { kind: 'unchanged', publishedVersion: after };
  }
  // An unchanged head can precede a still-running publish commit.
  return { kind: 'unknown' };
};

const functionRef = (companyId, functionId) => doc(db, `users/${companyId}/data_functions/${functionId}`);

const versionRef = (companyId, functionId, version) => doc(
  db,
  `users/${companyId}/data_functions/${functionId}/versions/${version}`,
);

// Read the head pointer fields a run surface needs. Returns null when the
// Function no longer exists.
export async function readFunctionHead(companyId, functionId, { fromServer = false } = {}) {
  const reference = functionRef(companyId, functionId);
  const snapshot = await (fromServer ? getDocFromServer(reference) : getDoc(reference));
  if (!snapshot.exists()) return null;
  const data = snapshot.data() || {};
  return {
    id: functionId,
    ...data,
    publishedVersion: isPublishedVersion(data.published_version) ? data.published_version : null,
    hasUnpublishedChanges: data.has_unpublished_changes === true,
  };
}

// Read one immutable version document. No torn read is possible: the whole
// executable graph is one document.
export async function loadFunctionVersion(companyId, functionId, version) {
  if (!isPublishedVersion(version)) return null;
  const snapshot = await getDoc(versionRef(companyId, functionId, version));
  if (!snapshot.exists()) return null;
  const content = snapshot.data()?.content;
  return isPlainObject(content) ? content : null;
}

// One head read plus one version read: everything a Run needs to build its
// confirmation, its parameter contract, and the version it will pin.
// `content` is null when the Function is unpublished or the version document is
// gone (the run would fail closed with `function_version_unavailable`).
export async function loadRunTarget(companyId, functionId) {
  const head = await readFunctionHead(companyId, functionId);
  if (!head) return null;
  const content = head.publishedVersion
    ? await loadFunctionVersion(companyId, functionId, head.publishedVersion)
    : null;
  return {
    head,
    publishedVersion: head.publishedVersion,
    hasUnpublishedChanges: head.hasUnpublishedChanges,
    invalid: head.invalid === true,
    invalidReason: head.invalid_reason || null,
    content,
  };
}

const versionBlocks = (content) => (isPlainObject(content?.blocks) ? content.blocks : {});

// The parameter contract of a published version: the data-source blocks and
// their filters, in the same shape the parameter surfaces already consume. A
// draft that renames or removes a filter changes nothing here until published.
export function versionDataSourceBlocks(content) {
  const blocks = {};
  Object.entries(versionBlocks(content)).forEach(([blockId, block]) => {
    if (block?.type !== 'data_source') return;
    blocks[blockId] = {
      name: block.name,
      table_id: block.config?.table_id,
      filters: block.config?.filters || [],
    };
  });
  return blocks;
}

// blockId -> name for every block of a published version. Result surfaces label
// a produced entry by the block that produced it, because an entry key names the
// slot ("data") while the block name is what the user actually wrote. A block id
// missing from this map is a block the current version no longer has, which the
// caller labels from the entry itself rather than inventing a name.
export function versionBlockNames(content) {
  const names = {};
  Object.entries(versionBlocks(content)).forEach(([blockId, block]) => {
    if (typeof block?.name === 'string' && block.name.trim() !== '') {
      names[blockId] = block.name;
    }
  });
  return names;
}

// The content of the version a RUN pinned. A version document is immutable, so
// it is read once per session and shared by every surface that labels that run.
// Resolves to null when the version is gone. Only content is kept: a failed
// read, or a null one (a document read a moment before it landed), is asked for
// again next time.
const versionContentCache = new Map();
export async function loadFunctionVersionCached(companyId, functionId, version) {
  if (!isPublishedVersion(version)) return null;
  const key = `${companyId}/${functionId}/${version}`;
  if (!versionContentCache.has(key)) {
    const promise = loadFunctionVersion(companyId, functionId, version);
    versionContentCache.set(key, promise);
    promise.then(
      (content) => { if (!content) versionContentCache.delete(key); },
      () => versionContentCache.delete(key),
    );
  }
  return versionContentCache.get(key);
}

export async function prepareHistoricalRunParameters(companyId, run, { functionId, version, content }) {
  if (!isPublishedVersion(version) || !isPlainObject(content)) {
    throw new Error('The current Function version could not be read. Reload it before reusing historical parameters.');
  }
  if (run?.function_id !== functionId || !isPublishedVersion(run?.function_version)) {
    throw new Error('This run has no verifiable Function version. Review the current parameters and start a new run.');
  }
  const previousContent = run.function_version === version
    ? content
    : await loadFunctionVersionCached(companyId, functionId, run.function_version);
  if (!previousContent) {
    throw new Error(`Version ${run.function_version} could not be read, so its parameters cannot be checked. Start a new run with the current parameters.`);
  }
  const previousBlocks = versionDataSourceBlocks(previousContent);
  const currentBlocks = versionDataSourceBlocks(content);
  const errors = parameterCompatibilityErrors(previousBlocks, currentBlocks);
  if (errors.length > 0) return { parameters: null, errors };

  // The effective values include fixed filters, which belong to the Function
  // definition and must not be submitted as variable parameters. Retain unknown
  // fields for validation instead of silently discarding malformed history.
  const hasEffectiveValues = run.resolved_parameters != null;
  const recorded = hasEffectiveValues ? run.resolved_parameters : (run.parameters ?? {});
  if (!hasEffectiveValues || !isPlainObject(recorded)) return prepareRunParameters(recorded, currentBlocks);
  const draft = {};
  Object.entries(recorded).forEach(([blockId, values]) => {
    if (!isPlainObject(values)) {
      draft[blockId] = values;
      return;
    }
    const block = {};
    const wasFixed = (column) => {
      const filter = (previousBlocks[blockId]?.filters || []).find((candidate) => candidate.column === column);
      return filter && !filter.variable;
    };
    Object.entries(values).forEach(([key, value]) => {
      if ((key === 'range_filters' || key === 'exclude_filters') && isPlainObject(value)) {
        block[key] = Object.fromEntries(Object.entries(value).filter(([column]) => !wasFixed(column)));
      } else if (!wasFixed(key)) {
        block[key] = value;
      }
    });
    draft[blockId] = block;
  });
  return prepareRunParameters(draft, currentBlocks);
}

// The block names of the version a RUN pinned, for labelling that run's tabs.
// Resolves to null when the version is gone (the caller then labels tabs by
// entry key rather than by the names of whatever the function publishes now).
export async function loadVersionBlockNames(companyId, functionId, version) {
  const content = await loadFunctionVersionCached(companyId, functionId, version);
  return content ? versionBlockNames(content) : null;
}

// The Email Output blocks of a published version, as the confirmation dialog
// needs them. Recipient contact ids are resolved against the contact book by the
// caller; names are formatted at the call site.
export function versionEmailOutputs(content) {
  const outputs = [];
  const blockNames = versionBlockNames(content);
  Object.entries(versionBlocks(content)).forEach(([blockId, block]) => {
    const config = block?.config || {};
    if (config.output_type !== 'email') return;
    outputs.push({
      blockId,
      nodeName: block.name || 'Email output',
      subject: config.subject || '',
      recipientContactIds: Array.isArray(config.recipient_contact_ids) ? config.recipient_contact_ids : [],
      // Two blocks commonly produce an entry under the same key ("data"), so an
      // attachment keeps its producing block as well as its key.
      attachments: (config.attachments || []).map((attachment) => ({
        sourceBlockId: attachment.source_block_id || null,
        blockName: blockNames[attachment.source_block_id] || null,
        entryKey: attachment.entry_key,
      })),
    });
  });
  return outputs;
}

// "Block name: entry" for the confirmation dialogs; `formatName` is the call
// site's text formatter. A block the version no longer names falls back to its
// id, and an unbound attachment to its key alone.
export function attachmentLabel(attachment, formatName = (value) => value) {
  const producer = attachment.blockName
    ? formatName(attachment.blockName)
    : attachment.sourceBlockId;
  return producer ? `${producer}: ${attachment.entryKey}` : String(attachment.entryKey ?? '');
}

// True when the published content contains an Output with an external effect.
// Effect detection is a pure function of version content — never of the draft.
export function versionHasExternalEffect(content) {
  return Object.values(versionBlocks(content)).some(
    (block) => outputHasExternalEffect(block?.config?.output_type),
  );
}

// --- Edit lease -------------------------------------------------------------
// The lease itself lives in services/editLease.js, shared with the View
// builder; these keep the Function-shaped signatures the builder calls.

export function acquireEditLease(companyId, functionId, actor) {
  return acquireLease(functionRef(companyId, functionId), actor, 'function');
}

export function takeOverEditLease(companyId, functionId, actor) {
  return takeOverLease(functionRef(companyId, functionId), actor, 'function');
}

export function heartbeatEditLease(companyId, functionId, sessionId) {
  return heartbeatLease(functionRef(companyId, functionId), sessionId);
}

export function releaseEditLease(companyId, functionId, sessionId) {
  return releaseLease(functionRef(companyId, functionId), sessionId, 'function');
}
